// src/pages/ProfileSetup.tsx — MULTI-STEP ONBOARDING
// Original logic 100% preserved — username check, completeProfileSetup, navigate
// New: step 2 (name/bio/location) + step 3 (interests) saved in same DB call
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import toast from 'react-hot-toast';
import {
  FaUser, FaSpinner, FaCheck, FaFire, FaBook,
  FaGamepad, FaPalette, FaMusic, FaStar,
  FaCode, FaVideo, FaArrowRight, FaMapMarkerAlt,
} from 'react-icons/fa';

// ── INTERESTS (mirrors ProfilePage) ─────────────────────────────
const AVAILABLE_INTERESTS = [
  { id: 'anime',     label: 'Anime',       icon: FaFire,    color: '#e8a820' },
  { id: 'manga',     label: 'Manga',       icon: FaBook,    color: '#a855f7' },
  { id: 'gaming',    label: 'Gaming',      icon: FaGamepad, color: '#3b82f6' },
  { id: 'art',       label: 'Digital Art', icon: FaPalette, color: '#10b981' },
  { id: 'music',     label: 'J-Pop/K-Pop', icon: FaMusic,   color: '#ec4899' },
  { id: 'cosplay',   label: 'Cosplay',     icon: FaStar,    color: '#f59e0b' },
  { id: 'coding',    label: 'Coding',      icon: FaCode,    color: '#6366f1' },
  { id: 'streaming', label: 'Streaming',   icon: FaVideo,   color: '#8b5cf6' },
];

interface ProfileSetupProps {
  darkMode?: boolean;
}

export default function ProfileSetup({ darkMode = true }: ProfileSetupProps) {
  // ── ORIGINAL STATE (untouched) ───────────────────────────────
  const [username, setUsername]                   = useState('');
  const [loading, setLoading]                     = useState(false);
  const [checkingUsername, setCheckingUsername]   = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);

  // ── NEW STATE ────────────────────────────────────────────────
  const [step, setStep]           = useState<1 | 2 | 3>(1);
  const [fullName, setFullName]   = useState('');
  const [bio, setBio]             = useState('');
  const [location, setLocation]   = useState('');
  const [interests, setInterests] = useState<string[]>([]);

  const { user, completeProfileSetup } = useAuth();
  const navigate = useNavigate();

  // ── ORIGINAL: username debounce check (untouched) ───────────
  useEffect(() => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingUsername(true);
      try {
        const { data, error } = await supabase
          .from('users')
          .select('username')
          .eq('username', username)
          .single();

        if (error && error.code === 'PGRST116') {
          setUsernameAvailable(true);
        } else if (data) {
          setUsernameAvailable(false);
        }
      } catch (error) {
        console.error('Error checking username:', error);
      } finally {
        setCheckingUsername(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [username]);

  // ── ORIGINAL: handleSubmit — extended to save all fields ─────
  const handleSubmit = async () => {
    if (!username || username.length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }
    if (!usernameAvailable) {
      toast.error('Username is already taken');
      return;
    }
    if (!user) {
      toast.error('Not authenticated');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          username,
          full_name:        fullName.trim()   || null,
          bio:              bio.trim()         || null,
          location:         location.trim()    || null,
          interests:        interests.length > 0 ? interests : null,
          profile_completed: true,
          updated_at:       new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Welcome to Ronyx! 🎉');
      await completeProfileSetup();
      navigate('/home');
    } catch (error: any) {
      console.error('Profile setup error:', error);
      toast.error(error.message || 'Failed to set up profile');
    } finally {
      setLoading(false);
    }
  };

  // ── STYLE TOKENS ─────────────────────────────────────────────
  const ink        = '#0d1a1a';
  const teal       = '#7ecece';
  const tealMid    = '#5ab8b8';
  const gold       = '#e8a820';
  const cream      = '#f0fafa';
  const cardBg     = '#0f2424';
  const pageBg     = '#0d1a1a';
  const border     = 'rgba(90,184,184,0.15)';
  const inputBg    = '#0d1a1a';
  const mutedText  = 'rgba(126,206,206,0.45)';

  const inputStyle: React.CSSProperties = {
    width: '100%',
    background: inputBg,
    border: `1.5px solid ${border}`,
    borderRadius: '10px',
    color: cream,
    fontFamily: "'M PLUS Rounded 1c', sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    padding: '12px 14px',
    outline: 'none',
    transition: 'border-color 0.15s',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '10px',
    fontWeight: 900,
    letterSpacing: '2px',
    color: mutedText,
    marginBottom: '6px',
    display: 'block',
  };

  const TOTAL_STEPS = 3; // eslint-disable-next-line
  const progress    = ((step - 1) / (TOTAL_STEPS - 1)) * 100;

  return (
    <div style={{
      minHeight: '100vh',
      background: pageBg,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 16px 60px',
      fontFamily: "'M PLUS Rounded 1c', sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=M+PLUS+Rounded+1c:wght@400;700;900&display=swap');
        @keyframes onyxSpin { to { transform: rotate(360deg); } }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .setup-step { animation: fadeUp 0.35s ease both; }
        .setup-input:focus { border-color: ${tealMid} !important; }
        .interest-btn:hover { border-color: rgba(90,184,184,0.4) !important; }
        .gold-btn:not(:disabled):hover {
          transform: translate(1px, 1px);
          box-shadow: 2px 2px 0 ${ink}, 3px 3px 0 #8a5a00 !important;
        }
        .ghost-btn:hover {
          border-color: rgba(90,184,184,0.35) !important;
          color: ${teal} !important;
        }
        @media (min-width: 480px) {
          .setup-card { padding: 40px 40px !important; }
        }
      `}</style>

      {/* Logo */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <div style={{
          width: 52, height: 52,
          background: ink,
          border: `2.5px solid rgba(90,184,184,0.3)`,
          borderRadius: '12px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 10px',
          boxShadow: '3px 3px 0 rgba(13,26,26,0.5)',
        }}>
          <span style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '24px', letterSpacing: '2px', color: teal,
          }}>R</span>
        </div>
        <p style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: '13px', letterSpacing: '4px', color: mutedText,
        }}>RONYX · PROFILE SETUP</p>
      </div>

      {/* Card */}
      <div
        className="setup-card"
        style={{
          width: '100%',
          maxWidth: '440px',
          background: cardBg,
          border: `1.5px solid ${border}`,
          borderRadius: '16px',
          boxShadow: '6px 6px 0 rgba(13,26,26,0.4)',
          padding: '32px 24px',
        }}
      >
        {/* Progress bar */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: '8px',
          }}>
            <span style={{ fontSize: '10px', fontWeight: 900, color: mutedText, letterSpacing: '2px' }}>
              STEP {step} OF {TOTAL_STEPS}
            </span>
            <span style={{ fontSize: '10px', fontWeight: 900, color: gold, letterSpacing: '1px' }}>
              {step === 1 ? 'USERNAME' : step === 2 ? 'PROFILE' : 'INTERESTS'}
            </span>
          </div>
          <div style={{
            width: '100%', height: '3px', borderRadius: '999px',
            background: 'rgba(90,184,184,0.1)',
          }}>
            <div style={{
              width: `${step === 1 ? 33 : step === 2 ? 66 : 100}%`,
              height: '100%', borderRadius: '999px',
              background: gold,
              transition: 'width 0.4s ease',
            }} />
          </div>
        </div>

        {/* ── STEP 1: USERNAME ── */}
        {step === 1 && (
          <div className="setup-step">
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '28px', letterSpacing: '2px',
              color: cream, marginBottom: '6px', lineHeight: 1,
            }}>
              CHOOSE YOUR USERNAME
            </h2>
            <p style={{ fontSize: '12px', fontWeight: 700, color: mutedText, marginBottom: '24px' }}>
              This is how others will find you on Ronyx. Can't be changed later.
            </p>

            {/* Username field */}
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>USERNAME</label>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: inputBg,
                border: `1.5px solid ${
                  usernameAvailable === true  ? '#10b981' :
                  usernameAvailable === false ? '#e05050' :
                  border
                }`,
                borderRadius: '10px',
                padding: '0 14px',
                transition: 'border-color 0.15s',
              }}>
                <FaUser style={{ color: mutedText, fontSize: '13px', flexShrink: 0 }} />
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder="your_handle"
                  maxLength={20}
                  required
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    color: cream,
                    fontFamily: "'M PLUS Rounded 1c', sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    padding: '12px 0',
                  }}
                />
                {checkingUsername && (
                  <FaSpinner style={{ color: tealMid, fontSize: '12px', animation: 'onyxSpin 0.8s linear infinite', flexShrink: 0 }} />
                )}
                {!checkingUsername && usernameAvailable === true && (
                  <FaCheck style={{ color: '#10b981', fontSize: '12px', flexShrink: 0 }} />
                )}
                {!checkingUsername && usernameAvailable === false && (
                  <span style={{ color: '#e05050', fontSize: '10px', fontWeight: 900, letterSpacing: '0.5px', flexShrink: 0 }}>TAKEN</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: mutedText }}>
                  lowercase, numbers, underscores only
                </span>
                <span style={{ fontSize: '10px', fontWeight: 700, color: mutedText }}>
                  {username.length}/20
                </span>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => {
                if (username.length < 3) { toast.error('Username must be at least 3 characters'); return; }
                if (!usernameAvailable) { toast.error('Username is already taken'); return; }
                setStep(2);
              }}
              disabled={!usernameAvailable || username.length < 3 || checkingUsername}
              className="gold-btn"
              style={{
                width: '100%', padding: '13px',
                background: gold,
                border: `2.5px solid ${ink}`,
                borderRadius: '8px',
                boxShadow: `4px 4px 0 ${ink}, 5px 5px 0 #8a5a00`,
                color: ink,
                fontFamily: "'M PLUS Rounded 1c', sans-serif",
                fontSize: '13px', fontWeight: 900, letterSpacing: '1.5px',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                transition: 'transform 0.1s, box-shadow 0.1s',
                opacity: (!usernameAvailable || username.length < 3 || checkingUsername) ? 0.45 : 1,
              }}
            >
              CONTINUE <FaArrowRight style={{ fontSize: '12px' }} />
            </button>
          </div>
        )}

        {/* ── STEP 2: NAME / BIO / LOCATION ── */}
        {step === 2 && (
          <div className="setup-step">
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '28px', letterSpacing: '2px',
              color: cream, marginBottom: '6px', lineHeight: 1,
            }}>
              TELL US ABOUT YOU
            </h2>
            <p style={{ fontSize: '12px', fontWeight: 700, color: mutedText, marginBottom: '24px' }}>
              All optional — you can fill this in later from your profile.
            </p>

            {/* Full name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>FULL NAME</label>
              <input
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                placeholder="Your real name (optional)"
                maxLength={50}
                className="setup-input"
                style={inputStyle}
              />
            </div>

            {/* Bio */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>BIO</label>
              <textarea
                value={bio}
                onChange={e => setBio(e.target.value)}
                placeholder="Describe yourself in a few words..."
                maxLength={160}
                rows={3}
                className="setup-input"
                style={{ ...inputStyle, resize: 'none' }}
              />
              <span style={{ fontSize: '10px', fontWeight: 700, color: mutedText, float: 'right', marginTop: '4px' }}>
                {bio.length}/160
              </span>
            </div>

            {/* Location */}
            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle}>LOCATION</label>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                background: inputBg,
                border: `1.5px solid ${border}`,
                borderRadius: '10px',
                padding: '0 14px',
              }}>
                <FaMapMarkerAlt style={{ color: mutedText, fontSize: '13px', flexShrink: 0 }} />
                <input
                  type="text"
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="City, Country (optional)"
                  maxLength={60}
                  className="setup-input"
                  style={{
                    flex: 1, background: 'transparent',
                    border: 'none', outline: 'none',
                    color: cream,
                    fontFamily: "'M PLUS Rounded 1c', sans-serif",
                    fontSize: '14px', fontWeight: 700,
                    padding: '12px 0',
                  }}
                />
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setStep(1)}
                className="ghost-btn"
                style={{
                  flex: '0 0 auto',
                  padding: '13px 18px',
                  background: 'transparent',
                  border: `1.5px solid ${border}`,
                  borderRadius: '8px',
                  color: mutedText,
                  fontFamily: "'M PLUS Rounded 1c', sans-serif",
                  fontSize: '12px', fontWeight: 900, letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                ← BACK
              </button>
              <button
                onClick={() => setStep(3)}
                className="gold-btn"
                style={{
                  flex: 1, padding: '13px',
                  background: gold,
                  border: `2.5px solid ${ink}`,
                  borderRadius: '8px',
                  boxShadow: `4px 4px 0 ${ink}, 5px 5px 0 #8a5a00`,
                  color: ink,
                  fontFamily: "'M PLUS Rounded 1c', sans-serif",
                  fontSize: '13px', fontWeight: 900, letterSpacing: '1.5px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                }}
              >
                CONTINUE <FaArrowRight style={{ fontSize: '12px' }} />
              </button>
            </div>

            {/* Skip */}
            <button
              onClick={() => setStep(3)}
              style={{
                width: '100%', marginTop: '12px',
                background: 'none', border: 'none',
                color: mutedText, fontSize: '11px', fontWeight: 700,
                letterSpacing: '1px', cursor: 'pointer',
              }}
            >
              SKIP FOR NOW
            </button>
          </div>
        )}

        {/* ── STEP 3: INTERESTS ── */}
        {step === 3 && (
          <div className="setup-step">
            <h2 style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: '28px', letterSpacing: '2px',
              color: cream, marginBottom: '6px', lineHeight: 1,
            }}>
              PICK YOUR INTERESTS
            </h2>
            <p style={{ fontSize: '12px', fontWeight: 700, color: mutedText, marginBottom: '20px' }}>
              Helps us tailor your feed. Pick as many as you want.
            </p>

            {/* Interests grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '8px',
              marginBottom: '24px',
            }}>
              {AVAILABLE_INTERESTS.map(interest => {
                const Icon    = interest.icon;
                const selected = interests.includes(interest.id);
                return (
                  <button
                    key={interest.id}
                    onClick={() => setInterests(prev =>
                      prev.includes(interest.id)
                        ? prev.filter(i => i !== interest.id)
                        : [...prev, interest.id]
                    )}
                    className="interest-btn"
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '12px 14px',
                      background: selected ? 'rgba(232,168,32,0.08)' : inputBg,
                      border: `1.5px solid ${selected ? gold : border}`,
                      borderRadius: '10px',
                      color: selected ? gold : mutedText,
                      fontFamily: "'M PLUS Rounded 1c', sans-serif",
                      fontSize: '12px', fontWeight: 900, letterSpacing: '0.5px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      boxShadow: selected ? `2px 2px 0 rgba(232,168,32,0.2)` : 'none',
                      textAlign: 'left',
                    }}
                  >
                    <Icon style={{ fontSize: '14px', color: selected ? gold : interest.color, flexShrink: 0 }} />
                    <span>{interest.label}</span>
                    {selected && (
                      <FaCheck style={{ marginLeft: 'auto', fontSize: '10px', color: gold, flexShrink: 0 }} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Selected count */}
            {interests.length > 0 && (
              <p style={{
                fontSize: '10px', fontWeight: 900, color: gold,
                letterSpacing: '1px', marginBottom: '16px', textAlign: 'center',
              }}>
                {interests.length} INTEREST{interests.length > 1 ? 'S' : ''} SELECTED
              </p>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setStep(2)}
                className="ghost-btn"
                style={{
                  flex: '0 0 auto',
                  padding: '13px 18px',
                  background: 'transparent',
                  border: `1.5px solid ${border}`,
                  borderRadius: '8px',
                  color: mutedText,
                  fontFamily: "'M PLUS Rounded 1c', sans-serif",
                  fontSize: '12px', fontWeight: 900, letterSpacing: '1px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                ← BACK
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="gold-btn"
                style={{
                  flex: 1, padding: '13px',
                  background: gold,
                  border: `2.5px solid ${ink}`,
                  borderRadius: '8px',
                  boxShadow: loading ? 'none' : `4px 4px 0 ${ink}, 5px 5px 0 #8a5a00`,
                  color: ink,
                  fontFamily: "'M PLUS Rounded 1c', sans-serif",
                  fontSize: '13px', fontWeight: 900, letterSpacing: '1.5px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  opacity: loading ? 0.55 : 1,
                }}
              >
                {loading ? (
                  <>
                    <FaSpinner style={{ animation: 'onyxSpin 0.8s linear infinite', fontSize: '13px' }} />
                    SETTING UP...
                  </>
                ) : (
                  <>
                    <FaCheck style={{ fontSize: '12px' }} />
                    ENTER RONYX
                  </>
                )}
              </button>
            </div>

            {/* Skip interests */}
            {!loading && (
              <button
                onClick={handleSubmit}
                style={{
                  width: '100%', marginTop: '12px',
                  background: 'none', border: 'none',
                  color: mutedText, fontSize: '11px', fontWeight: 700,
                  letterSpacing: '1px', cursor: 'pointer',
                }}
              >
                SKIP INTERESTS AND CONTINUE
              </button>
            )}
          </div>
        )}
      </div>

      {/* Step dots */}
      <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
        {[1, 2, 3].map(s => (
          <div
            key={s}
            style={{
              height: '4px',
              borderRadius: '999px',
              transition: 'width 0.3s, background 0.3s',
              width: step === s ? '20px' : '6px',
              background: step === s ? gold : 'rgba(90,184,184,0.2)',
            }}
          />
        ))}
      </div>

      {/* Bottom note */}
      <p style={{
        marginTop: '16px', fontSize: '11px', fontWeight: 700,
        color: 'rgba(126,206,206,0.25)', letterSpacing: '1px', textAlign: 'center',
      }}>
        YOU CAN EDIT ALL OF THIS LATER IN YOUR PROFILE
      </p>
    </div>
  );
}